import * as reservationsApi from "./reservations.apis";

type RequestOptions = { token?: string | null };

export type TableManagementSection = {
  id: string;
  restaurantId: string;
  name: string;
  description?: string;
  isActive: boolean;
  isArchived?: boolean;
};

export type TableManagementTable = {
  id: string;
  restaurantId: string;
  sectionId: string;
  name: string;
  capacity: number;
  combinableWith?: string[];
  notes?: string;
  isActive: boolean;
  isArchived?: boolean;
};

export type TableManagementGroup = {
  id: string;
  tableIds: string[];
  capacity?: number;
  notes?: string;
  isActive: boolean;
};

type ApiTableGroup = Awaited<ReturnType<typeof reservationsApi.listTableGroups>>["tableGroups"][number];

const normalizeId = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "_id" in value && typeof (value as any)._id === "string") {
    return (value as any)._id;
  }
  return "";
};

const normalizeGroup = (group: ApiTableGroup): TableManagementGroup => ({
  id: group._id,
  tableIds: (group.tableIds ?? []).map(normalizeId).filter(Boolean),
  capacity: group.capacity,
  notes: group.notes,
  isActive: !!group.isActive,
});

const normalizeSection = (section: reservationsApi.Section): TableManagementSection => ({
  id: section._id,
  restaurantId: section.restaurantId,
  name: section.name,
  description: section.description,
  isActive: !!section.isActive,
  isArchived: section.isArchived,
});

const normalizeTable = (table: reservationsApi.Table): TableManagementTable => ({
  id: table._id,
  restaurantId: table.restaurantId,
  sectionId: normalizeId(table.sectionId) || table.sectionId,
  name: table.name,
  capacity: table.capacity,
  combinableWith: table.combinableWith,
  notes: table.notes,
  isActive: table.isActive ?? true,
  isArchived: table.isArchived,
});

export const listSections = async (restaurantId: string, options?: RequestOptions): Promise<TableManagementSection[]> => {
  const response = await reservationsApi.listSections(restaurantId, options);
  return response.sections.map(normalizeSection).filter((s) => !s.isArchived);
};

export const createSection = async (
  restaurantId: string,
  body: { name: string; description?: string; isActive?: boolean },
  options?: RequestOptions
): Promise<TableManagementSection> => {
  const response = await reservationsApi.createSection(restaurantId, body, options);
  return normalizeSection(response.section);
};

export const updateSection = async (
  restaurantId: string,
  sectionId: string,
  body: { name?: string; description?: string; isActive?: boolean },
  options?: RequestOptions
): Promise<TableManagementSection> => {
  const response = await reservationsApi.updateSection(restaurantId, sectionId, body, options);
  return normalizeSection(response.section);
};

export const archiveSection = async (
  restaurantId: string,
  sectionId: string,
  options?: RequestOptions
): Promise<TableManagementSection> => {
  const response = await reservationsApi.archiveSection(restaurantId, sectionId, options);
  return normalizeSection(response.section);
};

export const listTables = async (
  restaurantId: string,
  query?: { sectionId?: string; active?: boolean },
  options?: RequestOptions
): Promise<TableManagementTable[]> => {
  const response = await reservationsApi.listTables(restaurantId, query, options);
  return response.tables.map(normalizeTable).filter((t) => !t.isArchived);
};

export const createTable = async (
  restaurantId: string,
  body: {
    name: string;
    capacity: number;
    sectionId: string;
    combinableWith?: string[];
    isActive?: boolean;
    notes?: string;
  },
  options?: RequestOptions
): Promise<TableManagementTable> => {
  const response = await reservationsApi.createTable(restaurantId, body, options);
  return normalizeTable(response.table);
};

export const updateTable = async (
  restaurantId: string,
  tableId: string,
  body: Partial<Pick<TableManagementTable, "name" | "capacity" | "sectionId" | "isActive" | "combinableWith" | "notes">>,
  options?: RequestOptions
): Promise<TableManagementTable> => {
  const response = await reservationsApi.updateTable(restaurantId, tableId, body, options);
  return normalizeTable(response.table);
};

export const archiveTable = async (
  restaurantId: string,
  tableId: string,
  options?: RequestOptions
): Promise<TableManagementTable> => {
  const response = await reservationsApi.archiveTable(restaurantId, tableId, options);
  return normalizeTable(response.table);
};

export const listTableGroups = async (
  restaurantId: string,
  query?: { active?: "true" | "false" | "all" },
  options?: RequestOptions
): Promise<TableManagementGroup[]> => {
  const response = await reservationsApi.listTableGroups(restaurantId, query, options);
  return response.tableGroups.map(normalizeGroup);
};

export const createTableGroup = async (
  restaurantId: string,
  body: { tableIds: string[]; notes?: string },
  options?: RequestOptions
): Promise<TableManagementGroup> => {
  const response = await reservationsApi.createTableGroup(restaurantId, body, options);
  return normalizeGroup(response.tableGroup as ApiTableGroup);
};

export const unmergeTableGroup = async (
  restaurantId: string,
  tableGroupId: string,
  options?: RequestOptions
): Promise<TableManagementGroup> => {
  const response = await reservationsApi.unmergeTableGroup(restaurantId, tableGroupId, options);
  return normalizeGroup(response.tableGroup as ApiTableGroup);
};

